/**
 * System Configuration API
 * MCP, Mem0, First Message, and Audit endpoints
 */

import apiClient from './client';
import type {
  MCPConfig,
  MCPServer,
  Mem0Config,
  Mem0Defaults,
  FirstMessageConfig,
  AuditLogResponse,
  ArtifactTypesConfig,
  ArtifactType,
  ArtifactTypeConfig,
  ToolEnhancementConfig,
  SystemPromptTemplatesConfig,
  SystemPromptTemplate,
  SystemPromptTemplateCreate,
  SystemPromptTemplateUpdate,
  ToolComboConfig,
  ToolComboTemplate,
  ToolComboTemplateSummary,
  ModelDefaultsConfig,
} from '@/types/system';

export const systemApi = {
  // MCP Configuration
  mcp: {
    /**
     * Get MCP configuration
     */
    get: async (): Promise<MCPConfig> => {
      const response = await apiClient.get('/system/mcp');
      return response.data;
    },

    /**
     * Update MCP configuration
     */
    update: async (config: MCPConfig): Promise<MCPConfig> => {
      const response = await apiClient.put('/system/mcp', config);
      return response.data;
    },

    /**
     * List MCP servers
     */
    listServers: async (): Promise<MCPServer[]> => {
      const response = await apiClient.get('/system/mcp/servers');
      // Backend returns { servers: [...], total: N }
      const serversData = response.data.servers || [];
      // Transform backend format to frontend MCPServer format
      return serversData.map((server: Record<string, unknown>) => {
        // Map backend transport types to frontend types
        // Backend supports: stdio, sse, streamable-http
        const backendTransport = server.transport as string || 'stdio';
        type TransportType = 'stdio' | 'sse' | 'streamable-http';
        const validTransports: TransportType[] = ['stdio', 'sse', 'streamable-http'];
        const transport: TransportType = validTransports.includes(backendTransport as TransportType)
          ? (backendTransport as TransportType)
          : 'stdio';

        return {
          id: server.id as string,
          name: (server.description as string) || (server.id as string),
          config: {
            command: server.command as string | undefined,
            args: server.args as string[] | undefined,
            url: server.url as string | undefined,
            transport,
            description: server.description as string | undefined,
            status: (server.status as 0 | 1) ?? 1,
          },
          status: (server.status === 1 || server.enabled) ? 'connected' as const : 'disconnected' as const,
          tools_count: server.tools_count as number | undefined,
        };
      });
    },

    /**
     * Test MCP server connection
     */
    testConnection: async (serverId: string): Promise<{ success: boolean; error?: string }> => {
      const response = await apiClient.post(`/system/mcp/test-connection/${serverId}`);
      // Backend returns { status: "success"|"error", connected: boolean, message: string, error?: string }
      // Frontend expects { success: boolean, error?: string }
      const data = response.data as { status: string; connected: boolean; message: string; error?: string };
      return {
        success: data.status === 'success' && data.connected,
        error: data.status === 'error' ? (data.error || data.message) : undefined,
      };
    },

    /**
     * Reload MCP manager
     */
    reload: async (): Promise<void> => {
      await apiClient.post('/system/mcp/reload');
    },
  },

  // Mem0 Configuration
  mem0: {
    /**
     * Get Mem0 configuration
     */
    get: async (): Promise<Mem0Config> => {
      const response = await apiClient.get('/system/mem0');
      return response.data;
    },

    /**
     * Update Mem0 configuration
     */
    update: async (config: Mem0Config): Promise<Mem0Config> => {
      const response = await apiClient.put('/system/mem0', config);
      return response.data;
    },

    /**
     * Get Mem0 defaults
     */
    getDefaults: async (): Promise<Mem0Defaults> => {
      const response = await apiClient.get('/system/mem0/defaults');
      return response.data;
    },
  },

  // First Message Configuration
  firstMessage: {
    /**
     * Get First Message configuration
     */
    get: async (): Promise<FirstMessageConfig> => {
      const response = await apiClient.get('/system/first-message');
      return response.data;
    },

    /**
     * Update First Message configuration
     */
    update: async (config: FirstMessageConfig): Promise<FirstMessageConfig> => {
      const response = await apiClient.put('/system/first-message', config);
      return response.data;
    },
  },

  // Audit Logs
  audit: {
    /**
     * Get all available audit types (dynamically discovered from database)
     */
    getTypes: async (): Promise<{ types: string[]; total: number }> => {
      const response = await apiClient.get('/system/audit/types');
      return response.data;
    },

    /**
     * Get audit logs for a specific type
     */
    get: async (type: string, limit = 50, offset = 0): Promise<AuditLogResponse> => {
      const response = await apiClient.get(`/system/audit/${type}`, {
        params: { limit, offset },
      });
      return response.data;
    },
  },

  // Artifact Types Configuration
  artifactTypes: {
    /**
     * Get artifact types configuration
     */
    get: async (): Promise<ArtifactTypesConfig> => {
      const response = await apiClient.get('/system/artifact-types');
      return response.data;
    },

    /**
     * Update artifact types configuration
     */
    update: async (config: ArtifactTypesConfig): Promise<ArtifactTypesConfig> => {
      const response = await apiClient.put('/system/artifact-types', config);
      return response.data;
    },

    /**
     * List artifact types
     */
    list: async (): Promise<ArtifactType[]> => {
      const response = await apiClient.get('/system/artifact-types/list');
      const typesData = response.data.types || [];
      return typesData.map((item: { type_id: string; config: ArtifactTypeConfig }) => ({
        id: item.type_id,
        config: item.config,
      }));
    },

    /**
     * Get a single artifact type
     */
    getType: async (typeId: string): Promise<ArtifactTypeConfig> => {
      const response = await apiClient.get(`/system/artifact-types/${typeId}`);
      return response.data;
    },

    /**
     * Add a new artifact type
     */
    addType: async (typeId: string, config: ArtifactTypeConfig): Promise<ArtifactTypeConfig> => {
      const response = await apiClient.post(`/system/artifact-types/${typeId}`, config);
      return response.data;
    },

    /**
     * Update an existing artifact type
     */
    updateType: async (typeId: string, config: ArtifactTypeConfig): Promise<ArtifactTypeConfig> => {
      const response = await apiClient.put(`/system/artifact-types/${typeId}`, config);
      return response.data;
    },

    /**
     * Delete an artifact type
     */
    deleteType: async (typeId: string): Promise<void> => {
      await apiClient.delete(`/system/artifact-types/${typeId}`);
    },
  },

  // Tool Enhancement Configuration
  toolEnhancement: {
    /**
     * Get tool enhancement configuration
     */
    get: async (): Promise<ToolEnhancementConfig> => {
      const response = await apiClient.get('/system/tool-enhancement');
      return response.data;
    },

    /**
     * Update tool enhancement configuration
     */
    update: async (config: ToolEnhancementConfig): Promise<ToolEnhancementConfig> => {
      const response = await apiClient.put('/system/tool-enhancement', config);
      return response.data;
    },

    /**
     * Get list of tool display names for a specific language
     */
    listTools: async (language: 'cn' | 'en' = 'cn'): Promise<Record<string, string>> => {
      const response = await apiClient.get('/system/tool-enhancement/tools', {
        params: { language },
      });
      return response.data.tools || {};
    },

    /**
     * Reload tool description enhancements from database into memory cache.
     * Similar to MCP manager reload, this syncs the database config to memory.
     */
    reload: async (): Promise<{ message: string; tools_loaded: number; note: string }> => {
      const response = await apiClient.post('/system/tool-enhancement/reload');
      return response.data;
    },
  },

  // System Prompt Templates Configuration
  promptTemplates: {
    /**
     * Get prompt templates configuration
     */
    get: async (): Promise<SystemPromptTemplatesConfig> => {
      const response = await apiClient.get('/system/prompt-templates');
      return response.data;
    },

    /**
     * List all templates sorted by display_order
     */
    list: async (category?: string): Promise<SystemPromptTemplate[]> => {
      const response = await apiClient.get('/system/prompt-templates/list', {
        params: category ? { category } : undefined,
      });
      return response.data.templates || [];
    },

    /**
     * Get list of unique categories
     */
    getCategories: async (): Promise<string[]> => {
      const response = await apiClient.get('/system/prompt-templates/categories');
      return response.data.categories || [];
    },

    /**
     * Get default templates (hardcoded)
     */
    getDefaults: async (): Promise<Record<string, SystemPromptTemplate>> => {
      const response = await apiClient.get('/system/prompt-templates/defaults');
      return response.data.templates || {};
    },

    /**
     * Get a single template by key
     */
    getByKey: async (key: string): Promise<SystemPromptTemplate> => {
      const response = await apiClient.get(`/system/prompt-templates/${key}`);
      return response.data;
    },

    /**
     * Create a new template
     */
    create: async (template: SystemPromptTemplateCreate): Promise<{ message: string; key: string }> => {
      const response = await apiClient.post('/system/prompt-templates', template);
      return response.data;
    },

    /**
     * Update an existing template
     */
    update: async (key: string, template: SystemPromptTemplateUpdate): Promise<{ message: string; key: string }> => {
      const response = await apiClient.put(`/system/prompt-templates/${key}`, template);
      return response.data;
    },

    /**
     * Delete a custom template (non-default only)
     */
    delete: async (key: string): Promise<void> => {
      await apiClient.delete(`/system/prompt-templates/${key}`);
    },

    /**
     * Reset a default template to its original value
     */
    reset: async (key: string): Promise<{ message: string; key: string }> => {
      const response = await apiClient.post(`/system/prompt-templates/${key}/reset`);
      return response.data;
    },
  },

  // Tool Combo Configuration
  toolCombo: {
    /**
     * Get tool combo configuration
     */
    get: async (): Promise<ToolComboConfig> => {
      const response = await apiClient.get('/system/tool-combo');
      return response.data;
    },

    /**
     * Update tool combo configuration
     */
    update: async (config: ToolComboConfig): Promise<ToolComboConfig> => {
      const response = await apiClient.put('/system/tool-combo', config);
      return response.data;
    },

    /**
     * List all templates (simplified)
     */
    list: async (enabledOnly = false, language = 'cn'): Promise<ToolComboTemplateSummary[]> => {
      const response = await apiClient.get('/system/tool-combo/templates', {
        params: { enabled_only: enabledOnly, language },
      });
      return response.data.templates || [];
    },

    /**
     * Get a single template by ID
     */
    getTemplate: async (templateId: string): Promise<ToolComboTemplate> => {
      const response = await apiClient.get(`/system/tool-combo/templates/${templateId}`);
      return response.data;
    },

    /**
     * Preview prompt context for a template
     */
    preview: async (templateId: string, language = 'cn'): Promise<{ context: string }> => {
      const response = await apiClient.post(`/system/tool-combo/templates/${templateId}/preview`, null, {
        params: { language },
      });
      // Backend returns { prompt_context, template_id }, map to { context }
      return { context: response.data.prompt_context || '' };
    },
  },

  // Model Defaults Configuration
  modelDefaults: {
    /**
     * Get model defaults configuration
     */
    get: async (): Promise<ModelDefaultsConfig> => {
      const response = await apiClient.get('/system/model-defaults');
      return response.data;
    },

    /**
     * Update model defaults configuration
     */
    update: async (config: ModelDefaultsConfig): Promise<{ message: string; version: number }> => {
      const response = await apiClient.put('/system/model-defaults', config);
      return response.data;
    },
  },
};
